import { TimeStampable } from '@/type-utils';
import { DateTime } from 'luxon';
import { exhaustiveGuard } from './function-utils';

/** Enumerates valid units of time. */
export enum TimeScale {
  Milliseconds,
  Seconds,
  Minutes,
  Hours,
  Days,
  Weeks,
  Months,
  Years
}

/**
 * Gets a date object representing when a resource should be considered expired and invalid.
 *
 * @param ttl - The number of units of time the resource is allowed to be considered valid.
 * @param [timeScale] - The unit of time to be used for `ttl`. Defaults to {@link TimeScale.Milliseconds}.
 * @param [fromDate] - An optional date to which the TTL will be applies from. Defaults to now.
 *
 * @returns A date object representing when a resource should be considered expired and invalid,
 * or Infinity if the resource should never expire.
 *
 * @throws An exception when an unknown `TimeScale` value is used at runtime.
 */
export const dateFromTTL = (
  ttl: number,
  timeScale: TimeScale = TimeScale.Milliseconds,
  fromDate: Date | undefined = undefined
): Date | typeof Infinity => {
  if (ttl === Infinity) return Infinity;

  let now = fromDate ? DateTime.fromJSDate(fromDate) : DateTime.now();

  switch (timeScale) {
    case TimeScale.Milliseconds:
      now = now.plus({ milliseconds: ttl });
      break;
    case TimeScale.Seconds:
      now = now.plus({ seconds: ttl });
      break;
    case TimeScale.Minutes:
      now = now.plus({ minutes: ttl });
      break;
    case TimeScale.Hours:
      now = now.plus({ hours: ttl });
      break;
    case TimeScale.Days:
      now = now.plus({ days: ttl });
      break;
    case TimeScale.Weeks:
      now = now.plus({ weeks: ttl });
      break;
    case TimeScale.Months:
      now = now.plus({ months: ttl });
      break;
    case TimeScale.Years:
      now = now.plus({ years: ttl });
      break;

    default:
      exhaustiveGuard(
        timeScale,
        `Unknown Timescale value "${timeScale}" passed to \`dateFromTTL\``
      );
  }

  return now.toJSDate();
};

/**
 * Given a {@link Timestampable} value, collapse it to a `Date`.
 * @param timeStampable - Any {@link Timestampable} value.
 * @returns An equivalent `Date`.
 */
export const timeStampableToDate = (timeStampable: TimeStampable): Date => {
  if (timeStampable instanceof Date) {
    return timeStampable;
  }

  return new Date(timeStampable);
};

/**
 * Given a {@link Timestampable} value, collapse it to an ISO string.
 * @param timeStampable - Any {@link Timestampable} value.
 * @returns An equivalent ISO string representing the datetime in question.
 */
export const timeStampableToISOString = (
  timeStampable: TimeStampable
): string => {
  if (timeStampable instanceof Date) {
    return timeStampable.toISOString();
  }

  return new Date(timeStampable).toISOString();
};

const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = MS_IN_SECOND * 60;
const MS_IN_HOUR = MS_IN_MINUTE * 60;
const MS_IN_DAY = MS_IN_HOUR * 24;
const MS_IN_WEEK = MS_IN_DAY * 7;
const MS_IN_MONTH = MS_IN_DAY * 30; // Approximation
const MS_IN_YEAR = MS_IN_DAY * 365; // Approximation

/**
 * Specification for discrete amounts of time in various units.
 * Any missing units are considered to be zero.
 */
export interface ITimeSpec {
  /** The number of milliseconds. */
  milliseconds?: number;
  /** The number of seconds. */
  seconds?: number;
  /** The number of minutes. */
  minutes?: number;
  /** The number of hours. */
  hours?: number;
  /** The number of days. */
  days?: number;
  /** The number of weeks. */
  weeks?: number;
  /** The number of months. */
  months?: number;
  /** The number of years. */
  years?: number;
}

/**
 * A helper class for working with time and discrete time units thereof.
 *
 * **NOTE**: This class does not account for calendar-based variations in time units, therefore
 * it is not suitable for date arithmetic where the exact date and time are important.
 *
 * Examples of where this class might not be appropriate include:
 * - Adding `Time.month(1)` to January 31st will result in March 3rd.
 * - Adding `Time.year(1)` to February 29th will result in February 28th.
 * - Adding `Time.month(12)` (360 days) to January 1st will result in December 27th.
 * - Adding `Time.hour(2)` to 1:30am on the day of daylight savings time will result in 2:30am.
 *
 * Typical use cases might include:
 * - Setting timeouts or intervals.
 * - Setting expiration times for cache entries or cookies.
 * - Setting durations for animations or transitions.
 *
 * Basically, if the exact date and time are not important, and you simply wish to
 * add a discrete amount of time (30 days, regardless of month) or convert one discrete amount of time
 * to another, this class is a good fit.
 *
 * @example
 * console.log(Time.seconds(1).milliseconds); // 1000
 * console.log(Time.minutes(1).seconds); // 60
 *
 * const minuteAndAHalf = Time.spec({ minutes: 1, seconds: 30 });
 * console.log(minuteAndAHalf.seconds); // 90
 * console.log(minuteAndAHalf.minutes); // 1
 */
export class Time {
  /**
   * Constructs a new `Time` object.
   * @param milliseconds - The number of milliseconds to represent.
   */
  private constructor(public readonly milliseconds: number) {}

  /**
   * @returns The number of seconds represented by the `Time` object,
   * rounded down to the nearest whole number.
   * @example
   * const time = Time.milliseconds(6500);
   * console.log(time.seconds); // 6
   */
  public get seconds(): number {
    return Math.trunc(this.milliseconds / MS_IN_SECOND);
  }

  /**
   * @returns The number of minutes represented by the `Time` object,
   * rounded down to the nearest whole number.
   * @example
   * const time = Time.seconds(65);
   * console.log(time.minutes); // 1
   */
  public get minutes(): number {
    return Math.trunc(this.milliseconds / MS_IN_MINUTE);
  }

  /**
   * @returns The number of hours represented by the `Time` object,
   * rounded down to the nearest whole number.
   * @example
   * const time = Time.minutes(125);
   * console.log(time.hours); // 2
   */
  public get hours(): number {
    return Math.trunc(this.milliseconds / MS_IN_HOUR);
  }

  /**
   * @returns The number of days represented by the `Time` object,
   * rounded down to the nearest whole number.
   * @example
   * const time = Time.hours(50);
   * console.log(time.days); // 2
   */
  public get days(): number {
    return Math.trunc(this.milliseconds / MS_IN_DAY);
  }

  /**
   * @returns The number of weeks represented by the `Time` object,
   * rounded down to the nearest whole number.
   * @example
   * const time = Time.days(15);
   * console.log(time.weeks); // 2
   */
  public get weeks(): number {
    return Math.trunc(this.milliseconds / MS_IN_WEEK);
  }

  /**
   * @returns The number of months represented by the `Time` object,
   * rounded down to the nearest whole number.
   *
   * **Note**: This will provide an approximate value as it
   * doesn't account for variations in month lengths.
   * All months are considered to have 30 days.
   *
   * @example
   * const time = Time.days(75);
   * console.log(time.months); // 2
   */
  public get months(): number {
    return Math.trunc(this.milliseconds / MS_IN_MONTH);
  }

  /**
   * @returns The number of years represented by the `Time` object,
   * rounded down to the nearest whole number.
   *
   * **Note**: This may provide an approximate value as it
   * doesn't account for leap years.
   *
   * @example
   * const time = Time.days(800);
   * console.log(time.years); // 2
   */
  public get years(): number {
    return Math.trunc(this.milliseconds / MS_IN_YEAR);
  }

  /**
   * Adds `this` and the given `Time` together.
   * @param value - The `Time` object to add.
   * @returns A new `Time` object representing the sum of the two `Time` objects.
   *
   * @example
   * const time1 = Time.milliseconds(1000);
   * const time2 = Time.milliseconds(2000);
   *
   * const sum = time1.add(time2);
   * console.log(sum.milliseconds); // 3000
   * console.log(sum.seconds); // 3
   */
  public add(value: Time): Time {
    return new Time(this.milliseconds + value.milliseconds);
  }

  /**
   * Subtracts the given `Time` from `this`.
   * @param value - The `Time` object to subtract.
   * @returns A new `Time` object representing the difference of the two `Time` objects.
   *
   * @example
   * const time1 = Time.milliseconds(3000);
   * const time2 = Time.milliseconds(2000);
   *
   * const difference = time1.subtract(time2);
   * console.log(difference.milliseconds); // 1000
   * console.log(difference.seconds); // 1
   */
  public subtract(value: Time): Time {
    return new Time(this.milliseconds - value.milliseconds);
  }

  /**
   * Multiplies the current `Time` by a given factor.
   * @param factor - The factor to multiply by.
   * @returns A new `Time` object representing the multiplied time.
   */
  public multiply(factor: number): Time {
    return new Time(this.milliseconds * factor);
  }

  /**
   * Returns the maximum of two `Time` objects.
   * @param firstTime - The first `Time` object.
   * @param times - Additional `Time` objects.
   * @returns The maximum `Time` object.
   */
  public static max(this: void, firstTime: Time, ...times: Array<Time>): Time {
    return times.reduce((maxTime, currentTime) => {
      return currentTime.milliseconds > maxTime.milliseconds
        ? currentTime
        : maxTime;
    }, firstTime);
  }

  /**
   * Returns the minimum of two `Time` objects.
   * @param firstTime - The first `Time` object.
   * @param times - Additional `Time` objects.
   * @returns The minimum `Time` object.
   */
  public static min(this: void, firstTime: Time, ...times: Array<Time>): Time {
    return times.reduce((minTime, currentTime) => {
      return currentTime.milliseconds < minTime.milliseconds
        ? currentTime
        : minTime;
    }, firstTime);
  }

  /**
   * Compares two `Time` objects.
   * @param time1 - The first `Time` object.
   * @param time2 - The second `Time` object.
   * @returns A negative number if `time1` is less than `time2`,
   * a positive number if `time1` is greater than `time2`,
   * or zero if they are equal.
   */
  public static compare(this: void, time1: Time, time2: Time): number {
    return time1.milliseconds - time2.milliseconds;
  }

  /**
   * Creates a `Time` object from a number of milliseconds.
   * @param ms - The number of milliseconds.
   * @returns A new `Time` object.
   */
  public static milliseconds(this: void, ms: number): Time {
    return new Time(ms);
  }

  /**
   * Creates a `Time` object from a number of seconds.
   * @param seconds - The number of seconds.
   * @returns A new `Time` object.
   */
  public static seconds(this: void, seconds: number): Time {
    return new Time(seconds * MS_IN_SECOND);
  }

  /**
   * Creates a `Time` object from a number of minutes.
   * @param minutes - The number of minutes.
   * @returns A new `Time` object.
   */
  public static minutes(this: void, minutes: number): Time {
    return new Time(minutes * MS_IN_MINUTE);
  }

  /**
   * Creates a `Time` object from a number of hours.
   * @param hours - The number of hours.
   * @returns A new `Time` object.
   */
  public static hours(this: void, hours: number): Time {
    return new Time(hours * MS_IN_HOUR);
  }

  /**
   * Creates a `Time` object from a number of days.
   * @param days - The number of days.
   * @returns A new `Time` object.
   */
  public static days(this: void, days: number): Time {
    return new Time(days * MS_IN_DAY);
  }

  /**
   * Creates a `Time` object from a number of weeks.
   * @param weeks - The number of weeks.
   * @returns A new `Time` object.
   */
  public static weeks(this: void, weeks: number): Time {
    return new Time(weeks * MS_IN_WEEK);
  }

  /**
   * Creates a `Time` object from a number of months.
   *
   * **Note**: This will provide an approximate `Time` value as it
   * doesn't account for variations in month lengths. All months are
   * considered to have 30 days.
   *
   * @param months - The number of months.
   * @returns A new `Time` object.
   */
  public static months(this: void, months: number): Time {
    return new Time(months * MS_IN_MONTH);
  }

  /**
   * Creates a `Time` object from a number of years.
   *
   * **Note**: This will provide an approximate `Time` value as it
   * doesn't account for leap years.
   *
   * @param years - The number of years.
   * @returns A new `Time` object.
   */
  public static years(this: void, years: number): Time {
    return new Time(years * MS_IN_YEAR);
  }

  /**
   * Gets the current Unix timestamp in seconds.
   * @returns The current time as a Unix timestamp (seconds since epoch).
   */
  public static nowInSeconds(this: void): number {
    return Math.trunc(Date.now() / 1000);
  }

  /**
   * Creates a `Time` object from a {@link ITimeSpec} object.
   *
   * **Note**: This may provide an approximate `Time` value as it
   * doesn't account for variations in month lengths or leap years.
   *
   * @param spec - The `ITimeSpec` object.
   * @returns A new `Time` object.
   * @example
   * const time = Time.spec({ minutes: 1, seconds: 30 });
   * console.log(time.seconds); // 90
   */
  public static spec(this: void, spec: ITimeSpec): Time {
    const {
      milliseconds = 0,
      seconds = 0,
      minutes = 0,
      hours = 0,
      days = 0,
      weeks = 0,
      months = 0,
      years = 0
    } = spec;

    const totalMs =
      milliseconds +
      seconds * MS_IN_SECOND +
      minutes * MS_IN_MINUTE +
      hours * MS_IN_HOUR +
      days * MS_IN_DAY +
      weeks * MS_IN_WEEK +
      months * MS_IN_MONTH +
      years * MS_IN_YEAR;

    return new Time(totalMs);
  }
}
